import React, { useCallback, useEffect, useState } from "react";
import { Plus, Save, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../lib/api";
import { AdminHeader, Panel, Row, Table, borderStyle, inputCls } from "../ui";
import MediaPicker from "../MediaPicker";
import SmartImage from "../../components/SmartImage";
import { PageLoader } from "../../components/States";

export default function CatalogueAdmin() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const [picker, setPicker] = useState(false);

  const load = useCallback(() => {
    adminApi
      .get("/admin/catalogue", { params: { search: q || undefined, limit: 500 } })
      .then((r) => setRows(r.data.items))
      .catch(() => setRows([]));
  }, [q]);

  useEffect(() => { load(); }, [load]);

  async function save() {
    try {
      if (edit.id) {
        await adminApi.put(`/admin/catalogue/${edit.id}`, {
          name: edit.name, description: edit.description, price: edit.price,
          image: edit.image, brand: edit.brand, page_slug: edit.page_slug,
          published: edit.published, order: Number(edit.order || 0),
        });
      } else {
        await adminApi.post("/admin/catalogue", edit);
      }
      toast.success("Saved");
      setEdit(null);
      load();
    } catch (err) {
      toast.error("Save failed", { description: err?.response?.data?.detail });
    }
  }

  async function remove(id) {
    if (!window.confirm("Delete this item?")) return;
    await adminApi.delete(`/admin/catalogue/${id}`);
    toast.success("Deleted");
    load();
  }

  return (
    <div data-testid="admin-catalogue">
      <AdminHeader title="Catalogue items" subtitle={rows ? `${rows.length} items across your category pages` : ""}>
        <div className="flex items-center gap-2 rounded-lg border px-3 py-1.5" style={borderStyle}>
          <Search className="h-4 w-4 gold-text" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search items" className="bg-transparent text-sm outline-none" data-testid="admin-catalogue-search" />
        </div>
        <button type="button" className="btn-gold" data-testid="admin-new-item-button"
          onClick={() => setEdit({ name: "", description: "", price: "", image: "", brand: "", page_slug: "", order: 0, published: true })}>
          <Plus className="h-4 w-4" /> New item
        </button>
      </AdminHeader>

      {edit && (
        <Panel className="mb-5">
          <div className="grid gap-4 lg:grid-cols-4">
            <div className="lg:col-span-1">
              <SmartImage src={edit.image} alt="" ratio="aspect-square" fit="contain" fallbackLabel={edit.name} />
              <button type="button" onClick={() => setPicker(true)} className="btn-outline-nq mt-2 w-full px-3 py-1.5 text-xs">Choose image</button>
            </div>
            <div className="grid gap-3 lg:col-span-3 sm:grid-cols-2">
              <input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} placeholder="Name" className={inputCls} style={borderStyle} data-testid="item-name-input" />
              <input value={edit.brand || ""} onChange={(e) => setEdit({ ...edit, brand: e.target.value })} placeholder="Brand" className={inputCls} style={borderStyle} />
              <input value={edit.price || ""} onChange={(e) => setEdit({ ...edit, price: e.target.value })} placeholder="Price e.g. $499" className={inputCls} style={borderStyle} data-testid="item-price-input" />
              <input value={edit.page_slug || ""} onChange={(e) => setEdit({ ...edit, page_slug: e.target.value })} placeholder="Page slug (e.g. efs-bullbars)" className={inputCls} style={borderStyle} />
              <textarea rows={3} value={edit.description || ""} onChange={(e) => setEdit({ ...edit, description: e.target.value })} placeholder="Description" className={`${inputCls} sm:col-span-2`} style={borderStyle} />
              <div className="flex items-center gap-3 sm:col-span-2">
                <button type="button" onClick={save} className="btn-gold" data-testid="item-save-button"><Save className="h-4 w-4" /> Save</button>
                <button type="button" onClick={() => setEdit(null)} className="btn-outline-nq">Cancel</button>
                <label className="ml-auto flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={!!edit.published} onChange={(e) => setEdit({ ...edit, published: e.target.checked })} className="accent-[color:var(--nq-gold)]" />
                  Published
                </label>
              </div>
            </div>
          </div>
        </Panel>
      )}

      {!rows ? <PageLoader /> : (
        <Table head={["", "Name", "Brand", "Price", "Page", ""]}>
          {rows.map((it) => (
            <Row key={it.id} testid="admin-item-row">
              <td className="w-16 px-4 py-2">
                <div className="w-10"><SmartImage src={it.image} alt="" ratio="aspect-square" fit="contain" fallbackLabel={it.name} /></div>
              </td>
              <td className="px-4 py-3 font-medium">{it.name}</td>
              <td className="px-4 py-3 text-xs text-[color:var(--nq-stone)]">{it.brand || "-"}</td>
              <td className="px-4 py-3 font-mono text-xs gold-text">{it.price || "Enquiry"}</td>
              <td className="px-4 py-3 font-mono text-xs text-[color:var(--nq-stone)]">{it.page_slug}</td>
              <td className="px-4 py-3">
                <div className="flex gap-1">
                  <button type="button" onClick={() => setEdit(it)} className="btn-outline-nq px-2 py-1 text-xs" data-testid="admin-item-edit">Edit</button>
                  <button type="button" onClick={() => remove(it.id)} className="rounded p-1.5 hover:bg-white/10" data-testid="admin-item-delete">
                    <Trash2 className="h-4 w-4" style={{ color: "#e07a72" }} />
                  </button>
                </div>
              </td>
            </Row>
          ))}
        </Table>
      )}

      {picker && <MediaPicker onClose={() => setPicker(false)} onPick={(m) => { setEdit((v) => ({ ...v, image: m.url })); setPicker(false); }} />}
    </div>
  );
}
